import { useEffect, useState } from 'react';
import { AiGenerateDialog } from '@/components/ai-job/AiGenerateDialog';

interface GenerateDescriptionsDialogProps {
  attractionId: string;
  themeId: string;
  open: boolean;
  onClose: () => void;
}

/**
 * Generates themed descriptions for an attraction theme. Routes through the
 * on-demand AI-job pipeline so the user can hand off to background and
 * receive a global notification on completion.
 */
export function GenerateDescriptionsDialog({
  attractionId,
  themeId,
  open,
  onClose,
}: GenerateDescriptionsDialogProps) {
  const [descriptionType, setDescriptionType] = useState<
    'both' | 'description' | 'long_description'
  >('both');

  // Reset to default each time the dialog opens.
  useEffect(() => {
    if (open) setDescriptionType('both');
  }, [open]);

  return (
    <AiGenerateDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title="Generate Themed Descriptions"
      submitLabel="Generate Descriptions"
      description={
        <span className="space-y-2 block">
          <span className="block">
            Generate AI-powered themed descriptions for this attraction. The
            descriptions will be written in the style and tone of the selected
            theme type.
          </span>
          <span className="block text-sm text-muted-foreground">
            Short: ~200 words | Long: ~600 words
          </span>
          <span className="block text-destructive font-medium">
            WARNING: This will overwrite existing descriptions based on the selected type.
          </span>
        </span>
      }
      endpoint={`/attractions/${attractionId}/themes/${themeId}/generate-descriptions`}
      invalidateKeys={[['AttractionTheme', attractionId, themeId]]}
      successMessage="Themed descriptions generated successfully"
      errorFallback="Failed to generate themed descriptions"
      buildPayload={() => ({ descriptionType })}
    >
      <div>
        <label htmlFor="descriptionType" className="block text-sm font-medium mb-2">
          Description Type <span className="text-destructive">*</span>
        </label>
        <select
          id="descriptionType"
          required
          className="w-full px-3 py-2 border rounded-md"
          value={descriptionType}
          onChange={(e) =>
            setDescriptionType(
              e.target.value as 'both' | 'description' | 'long_description',
            )
          }
        >
          <option value="both">Both (Short + Long)</option>
          <option value="description">Short Description Only</option>
          <option value="long_description">Long Description Only</option>
        </select>
        <p className="text-xs text-muted-foreground mt-1">
          Choose which descriptions to generate
        </p>
      </div>
    </AiGenerateDialog>
  );
}
